import React from 'react';
import { ScrollView, StyleSheet, View, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { WebConfirmModal } from '@/components/common/modals';
import { GlassCard } from '@/components/shared/redesign';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin/desktop';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

interface DesktopFormPageProps {
  title: string;
  subtitle?: string;
  isSubmitting?: boolean;
  submitText?: string;
  onSubmit: () => void;
  onCancel: () => void;
  confirmDiscardOnWeb?: boolean;
  discardTitle?: string;
  discardMessage?: string;
  children: React.ReactNode;
  contentMaxWidth?: number;
  renderHeaderActions?: () => React.ReactNode;
}

export const DesktopFormPage: React.FC<DesktopFormPageProps> = ({
  title,
  subtitle,
  isSubmitting,
  submitText = 'Save',
  onSubmit,
  onCancel,
  confirmDiscardOnWeb = true,
  discardTitle = DESKTOP_CONFIRM_COPY.discardTitle,
  discardMessage = DESKTOP_CONFIRM_COPY.discardMessage,
  children,
  contentMaxWidth = 880,
  renderHeaderActions,
}) => {
  const styles = useStyles();
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);

  const handleCancelPress = () => {
    if (confirmDiscardOnWeb && Platform.OS === 'web') {
      setShowDiscardModal(true);
      return;
    }
    onCancel();
  };

  return (
    <DesktopAdminLayout>
      {confirmDiscardOnWeb && (
        <WebConfirmModal
          visible={showDiscardModal}
          title={discardTitle}
          message={discardMessage}
          confirmText={DESKTOP_CONFIRM_COPY.discardConfirmText}
          cancelText={DESKTOP_CONFIRM_COPY.discardCancelText}
          onConfirm={() => {
            setShowDiscardModal(false);
            onCancel();
          }}
          onCancel={() => setShowDiscardModal(false)}
        />
      )}

      <DesktopPageHeader title={title} subtitle={subtitle} showAddButton={false}>
        {renderHeaderActions ? (
          renderHeaderActions()
        ) : (
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <Hoverable hover="glass" onPress={handleCancelPress} style={styles.secondaryBtn}>
              <Text style={styles.secondaryText}>Cancel</Text>
            </Hoverable>
            <Hoverable
              hover="button"
              onPress={onSubmit}
              disabled={isSubmitting}
              style={[styles.primaryBtn, isSubmitting ? { opacity: 0.7 } : null]}
            >
              <Text style={styles.primaryText}>{submitText}</Text>
            </Hoverable>
          </View>
        )}
      </DesktopPageHeader>

      <ScrollView
        style={styles.desktopScroll}
        contentContainerStyle={[
          styles.desktopScrollContent,
          { maxWidth: contentMaxWidth, alignSelf: 'center', width: '100%' },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <GlassCard padding={24} style={styles.formCard}>
          {children}
        </GlassCard>
      </ScrollView>
    </DesktopAdminLayout>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      desktopScroll: {
        flex: 1,
      },
      desktopScrollContent: {
        paddingHorizontal: 16,
        paddingBottom: 24,
        gap: 16,
      },
      formCard: {
        gap: 16,
      },
      secondaryBtn: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      secondaryText: {
        color: admin.dataTable.text,
        fontWeight: '600',
      },
      primaryBtn: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: admin.formInput.borderFocused,
      },
      primaryText: {
        color: '#FFFFFF',
        fontWeight: '600',
      },
    });
  });

// Note: GlassCard does not natively merge `gap` into its inner padding container,
// so callers using FormSection-style children inside DesktopFormPage will see
// 16px between siblings via the wrapper's `gap` style above.
